import { Namespace, TFunction } from "i18next";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Info } from "@/assets/icons/icons";
import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerData } from "@/hooks/useCustomerState";
import { useTwoFactorMethodFromQueryParam } from "@/hooks/useTwoFactorMethodFromQueryParam.ts";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetCustomer } from "@/services/api";
import { authenticate } from "@/services/auth";
import {
  BasicCustomerDataTwoFactorMethod,
  ValidateTwoFactorAuthenticationMethod,
} from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import { getMagicLoginEnabled } from "@/utils/magicLoginPermission";
import { TWO_FACTOR_ERROR_MESSAGES } from "@/utils/twoFactorErrorHandler";

import { TwoFactorCodeModal } from "./TwoFactorCodeModal";

export const expectedAuthTypeQueryParam = "expectedAuthType";

/**
 * Displays the current 2fa status and a link to change that status.
 * Determines status by combining query parameters (we might expect a change that the backend does not return yet)
 * and the backend state.
 */
export const TwoFactorAuthStatus: FCC = () => {
  const { t } = useTranslation();
  const twoFactorMethod = useTwoFactorMethodFromQueryParam();
  const { customerData } = useCustomerData();
  const userAccountState = useAtomValue(userAccountStateAtom);
  // Same query as MagicLinkLoginSetting's - react-query dedupes it, this
  // doesn't add a second request when both cards are mounted together.
  const {
    data: expandedCustomer,
    isLoading: isLoadingExpandedCustomer,
    isError: isExpandedCustomerError,
  } = useGetCustomer({ expanded: true });

  const showAlert = useAlert();

  const [showModal, setShowModal] = useState(false);
  const [hasRequestedSms, setHasRequestedSms] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | undefined>(
    undefined,
  );
  const hasAnyTwoFactorAuth =
    twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD;
  // Mirrors MagicLinkLoginSetting's own hasTwoFactorEnabled-disables-magicLink
  // rule in reverse: the two login methods are mutually exclusive, so magic
  // link being on locks this card the same way 2FA being on locks that one.
  const isMagicLinkEnabled = getMagicLoginEnabled(expandedCustomer);

  const twoFactorMethodText = getTwoFactorAuthText(t, twoFactorMethod);
  const twoFactorCodeRedirect = useTwoFactorCodeRedirect();

  // The E-Mail and Passwort cards already hide each other's trigger while one of
  // them is being edited. Entering the 2FA flow from here would navigate away and
  // drop that unsaved input, so this action is locked for the same states.
  const isEditingLoginData =
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    userAccountState.editingPersonalData;
  // Magic Link and 2FA are mutually exclusive (see isMagicLinkEnabled above) -
  // while the Magic Link state is loading or failed to load, allowing this
  // action risks enabling 2FA on top of an active Magic Link.
  const isMagicLinkStateUnknown =
    isLoadingExpandedCustomer || isExpandedCustomerError;
  const isDisabled =
    isEditingLoginData || isMagicLinkEnabled || isMagicLinkStateUnknown;

  return (
    <ProfileCard
      title={
        (hasAnyTwoFactorAuth ? twoFactorMethodText : undefined) ??
        t("portal:user-account.security.2fa.title")
      }
      subtitle={
        hasAnyTwoFactorAuth
          ? t("portal:user-account.security.2fa.title")
          : undefined
      }
      stickyHeader={false}
      maxWidth="none"
      actions={
        <>
          {showModal && (
            <TwoFactorCodeModal
              userName={customerData?.email}
              setShowModal={setShowModal}
              method={twoFactorMethod}
              successAction={twoFactorCodeRedirect}
              type="change_2fa"
              twoFactorGrantToken={twoFactorToken}
              variant="dark"
            />
          )}

          {hasAnyTwoFactorAuth ? (
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled={isDisabled}
              onClick={() => {
                if (hasRequestedSms) {
                  setShowModal(true);

                  return;
                }
                authenticate()
                  .then((response) => {
                    setTwoFactorToken(response?.tempToken);
                    setHasRequestedSms(true);
                    setShowModal(true);
                  })
                  .catch(() => {
                    showAlert({
                      text: t(TWO_FACTOR_ERROR_MESSAGES.SERVER_ERROR),
                      type: AlertTypes.error,
                    });
                  });
              }}
            >
              {t("portal:user-account.security.buttons.manage")}
            </Button>
          ) : (
            <ButtonLink
              variant="primary"
              size="sm"
              to={portalRoutes.user.security.initialSetup}
              disabled={isDisabled}
            >
              {t("portal:user-account.security.buttons.activate")}
            </ButtonLink>
          )}
        </>
      }
    >
      {hasAnyTwoFactorAuth ? null : (
        <p className="text-base text-foreground-primary">
          <span>{t("portal:user-account.security.2fa.description")}</span>{" "}
          <a
            className="text-foreground-primary underline"
            href={t("portal:user-account.security.2fa.readMoreUrl")}
            target="_blank"
            rel="noreferrer"
          >
            {t("portal:user-account.security.2fa.readMore")}
          </a>
        </p>
      )}

      {isMagicLinkEnabled && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-surface-tertiary p-4">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-surface-info">
            <Info className="h-4 w-4 text-foreground-primary-inverse" />
          </span>
          <span className="text-base font-semibold text-foreground-primary">
            {t("portal:user-account.security.2fa.disabledBecauseMagicLink")}
          </span>
        </div>
      )}
    </ProfileCard>
  );
};

const getTwoFactorAuthText = (
  t: TFunction<Namespace, undefined, Namespace>,
  authType?: BasicCustomerDataTwoFactorMethod,
) => {
  if (authType === BasicCustomerDataTwoFactorMethod.APP) {
    return t("portal:user-account.security.2fa.method.app");
  } else if (authType === BasicCustomerDataTwoFactorMethod.SMS) {
    return t("portal:user-account.security.2fa.method.sms");
  }
};

/**
 * Returns a function to handle navigation based on the two-factor
 * authentication method specified in the query parameters. Depending on the method (SMS or App),
 * it will navigate to the relevant 2FA change page.
 */
const useTwoFactorCodeRedirect = () => {
  const navigate = useNavigate();
  const method = useTwoFactorMethodFromQueryParam();

  return () => {
    const hasSmsTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.SMS;
    const hasAppTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.APP;

    if (hasAppTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faApp);
    }
    if (hasSmsTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faSms);
    }
  };
};
